import { createFileRoute, notFound, Outlet } from "@tanstack/react-router"
import { GatedSessionDetailsFailReason } from "ipctg-inflow-types/InFlowEnums"

import api from "@/utilities/api"

import { Route as QrQrLookupIDGatedPamIDLookUpChallengeImport } from "@/routes/qr/$qrLookupID/gated/$pamID/look-up.challenge"
import { Route as QrQrLookupIDGatedPamIDLookUpIndexImport } from "@/routes/qr/$qrLookupID/gated/$pamID/look-up.index"
import { ExpectedError } from "@/utilities/expectedErrors"
import { NOT_FOUND_SESSION } from "@/utilities/globals"
import { sessionDetailsQueryOptions } from "@/utilities/hooks/queries/express"
import { log } from "@/utilities/logging"

export const Route = createFileRoute("/qr/$qrLookupID/gated/$pamID/$sessionID")(
  {
    beforeLoad: async ({ context, params, matches }) => {
      /*
        If PAM detects their license plate and there is an active
        unregistered session it directs them to the $pamID/$sessionID index
        page and gets redirected to the cart page so we need to fetch the
        welcome token.

        If we are navigating to the cart page, and its from the license or
        phone lookups, we should already have a welcome token.

        We don't do any checks if the user is on the session index page since
        it just redirects the user to the cart page. Why don't we fetch the
        token there? Not sure if there was a bug or some other issue, but the
        welcome tokens were getting lost after the redirect so we fetch it on
        the destination page (cart/lookup) instead.
      */

      const sessionRootMatch = matches.find(
        (m) => m.routeId === "/qr/$qrLookupID/gated/$pamID/$sessionID/"
      )

      if (sessionRootMatch) return

      const cartPageMatch = matches.find(
        (m) => m.routeId === "/qr/$qrLookupID/gated/$pamID/$sessionID/cart"
      )

      if (
        cartPageMatch &&
        ![
          QrQrLookupIDGatedPamIDLookUpIndexImport.id,
          QrQrLookupIDGatedPamIDLookUpChallengeImport.id,
        ].some((id) => id === cartPageMatch.search.from)
      ) {
        context.queryClient.clear()
        await api.welcome.POST({ qr: params.qrLookupID })
      }

      try {
        await context.queryClient.fetchQuery(
          sessionDetailsQueryOptions({ sessionID: params.sessionID })
        )
      } catch (error) {
        if (
          error instanceof ExpectedError &&
          error.failReason === GatedSessionDetailsFailReason.SessionNotFound
        ) {
          log({
            level: "warn",
            flow: "gated",
            scope: "guard.session",
            message: "Session not found, showing not-found page",
            meta: { sessionID: params.sessionID },
          })
          throw notFound({
            data: {
              type: NOT_FOUND_SESSION,
            },
          })
        }
        log({
          level: "error",
          flow: "gated",
          scope: "guard.session",
          message: "Failed to load session details",
          meta: { sessionID: params.sessionID, error },
        })
        throw error
      }
    },
    shouldReload: true,
    component: Outlet,
  }
)
